
import { useState } from 'react';
import { Doctor } from '@/types';
import { doctorService } from '@/services/doctorService';
import { sampleRecordCreation } from '@/services/recordCreationHelper';
import { toast } from '@/components/ui/sonner';
import { DEFAULT_DOCTOR } from './useDoctorFormState';

export const useDoctorActions = (
  setDoctors: React.Dispatch<React.SetStateAction<Doctor[]>>,
  setConnectionError: React.Dispatch<React.SetStateAction<boolean>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setIsEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setIsDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setIsScheduleDialogOpen: React.Dispatch<React.SetStateAction<boolean>>, // Added this parameter
  newDoctor: Omit<Doctor, 'id'>,
  currentDoctor: Doctor | null,
  imageFile: File | null,
  resetForm: () => void
) => {

  // Add doctor handler
  const handleAddDoctor = async () => {
    try {
      setIsLoading(true);
      
      // Use FormData to handle file upload
      const formData = new FormData();
      formData.append('name', newDoctor.name);
      formData.append('specialty', newDoctor.specialty);
      formData.append('bio', newDoctor.bio || '');
      formData.append('weeklyOffDays', JSON.stringify(newDoctor.weeklyOffDays || []));
      formData.append('slotDuration', String(newDoctor.slotDuration || 30));
      formData.append('startTime', newDoctor.startTime || '09:00');
      formData.append('endTime', newDoctor.endTime || '17:00');
      
      // Add image file if available, otherwise use the URL
      if (imageFile) {
        formData.append('profileImage', imageFile);
      } else if (newDoctor.image) {
        formData.append('image', newDoctor.image);
      }
      
      const doctorToAdd = await doctorService.create(formData);
      
      // Update the doctors list with the new doctor
      setDoctors(prevDoctors => [...prevDoctors, doctorToAdd]);
      setConnectionError(false); // Reset connection error state if successful
      
      setIsEditDialogOpen(false);
      toast(`${newDoctor.name} has been added to the system.`);
      resetForm();
    } catch (error) {
      console.error('Error adding doctor:', error);
      toast("Failed to add doctor. Please check your database connection.");
    } finally {
      setIsLoading(false);
    }
  };

  // Edit doctor handler
  const handleEditDoctor = (doctor: Doctor, setCurrentDoctor: React.Dispatch<React.SetStateAction<Doctor | null>>, setNewDoctor: React.Dispatch<React.SetStateAction<Omit<Doctor, 'id'>>>, setImagePreview: React.Dispatch<React.SetStateAction<string>>) => {
    setCurrentDoctor(doctor);
    setNewDoctor(doctor);
    setImagePreview(doctor.image || '');
    setIsEditDialogOpen(true);
  };
  
  // Update doctor handler
  const handleUpdateDoctor = async () => {
    if (!currentDoctor) return;
    
    try {
      setIsLoading(true);
      
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('name', newDoctor.name);
      formData.append('specialty', newDoctor.specialty);
      formData.append('bio', newDoctor.bio || '');
      formData.append('weeklyOffDays', JSON.stringify(newDoctor.weeklyOffDays || []));
      formData.append('slotDuration', String(newDoctor.slotDuration || 30));
      formData.append('startTime', newDoctor.startTime || '09:00');
      formData.append('endTime', newDoctor.endTime || '17:00');
      
      // Add image file if available, otherwise use the URL
      if (imageFile) {
        formData.append('profileImage', imageFile);
      } else if (newDoctor.image) {
        formData.append('image', newDoctor.image);
      }
      
      const updatedDoctor = await doctorService.update(currentDoctor.id, formData);
      
      // Update the doctors list with the updated doctor
      setDoctors(prevDoctors => prevDoctors.map(doc => 
        doc.id === currentDoctor.id ? updatedDoctor : doc
      ));
      setConnectionError(false); // Reset connection error state if successful
      
      setIsEditDialogOpen(false);
      toast(`${newDoctor.name}'s information has been updated.`);
      resetForm();
    } catch (error) {
      console.error('Error updating doctor:', error);
      toast("Failed to update doctor. Please check your database connection.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete prompt handler
  const handleDeletePrompt = (doctor: Doctor, setCurrentDoctor: React.Dispatch<React.SetStateAction<Doctor | null>>) => {
    setCurrentDoctor(doctor);
    setIsDeleteDialogOpen(true);
  };
  
  // Delete doctor handler
  const handleDeleteDoctor = async () => {
    if (!currentDoctor) return;
    
    try {
      setIsLoading(true);
      await doctorService.delete(currentDoctor.id);
      
      // Update the doctors list by removing the deleted doctor
      setDoctors(prevDoctors => prevDoctors.filter(doc => doc.id !== currentDoctor.id));
      setConnectionError(false); // Reset connection error state if successful
      
      setIsDeleteDialogOpen(false);
      toast(`${currentDoctor.name} has been removed from the system.`);
    } catch (error) {
      console.error('Error deleting doctor:', error);
      toast("Failed to delete doctor. Please check your database connection.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Schedule management handler
  const handleScheduleManagement = (doctor: Doctor, setCurrentDoctor: React.Dispatch<React.SetStateAction<Doctor | null>>) => {
    setCurrentDoctor(doctor);
    setIsScheduleDialogOpen(true); // This is the line that was causing the error
  };
  
  // Create sample records handler
  const handleCreateSampleRecords = async () => {
    try {
      setIsLoading(true);
      const { doctor, patient, appointment } = await sampleRecordCreation();
      
      if (doctor) {
        // Add the newly created doctor to the existing list
        setDoctors(prevDoctors => [...prevDoctors, doctor]);
        setConnectionError(false); // Reset connection error state if successful
      }
      
    } catch (error) {
      toast("Error creating sample records. There was a problem creating the sample records.");
      console.error('Error creating sample records:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    handleAddDoctor,
    handleEditDoctor,
    handleUpdateDoctor,
    handleDeletePrompt,
    handleDeleteDoctor,
    handleScheduleManagement,
    handleCreateSampleRecords
  };
};
